import { AvailabilityDayOfWeekInterval, AvailabilityTimeOfDayInterval, CareType, MatchingProvidersType, PublicAppointment } from '@uplift/api';
import { AgeGroupEnum, ProviderFilterTypeEnum } from '@uplift/types';

import { BookingForEnum } from '../types';

export const enum MatchingFlowTypeEnum {
  Marketplace = 'marketplace',
  Direct = 'direct',
  Invite = 'invite',
}

export type OnboardingMachineContext = {
  isLoggedIn: boolean | null;
  isSlotUnavailable: boolean;
  shouldShowWelcomeStep: boolean;
  userId: string | null;
  matchId: string | null;
  seed: number | null;
  stateId: string | null;
  insuranceId: string | null;
  who: BookingForEnum | null;
  modalityId: string | null;
  age: { id: string; ageGroup: AgeGroupEnum } | null;
  careType: CareType | null;
  recommendedCareType: CareType | null;
  isUnsureAboutCareType: boolean;
  skipCareType: boolean;
  specialtyIds: string[];

  showTheseProviderFilters: ProviderFilterTypeEnum[];
  identityFilters: {
    genderIds: string[];
    faithIds: string[];
    ethnicityIds: string[];
  };
  experienceFilters: {
    communityIds: string[];
    faithIds: string[];
    ethnicityIds: string[];
  };
  availabilityTimeOfDay: AvailabilityTimeOfDayInterval[];
  availabilityDayOfWeek: AvailabilityDayOfWeekInterval[];

  phqScore1: number | null;
  phqScore2: number | null;
  gadScore1: number | null;
  gadScore2: number | null;
  hasMedHistory: boolean | null;
  hasTherapist: boolean | null;
  isTherapistGoodFit: boolean | null;

  termsAccepted: boolean;

  intakeFormSubmissionId: string | null;
  /**
    * Selected provider id for choosing a provider from matching list
    */
  selectedProviderId: string | null;
  appointment: PublicAppointment | null;
  appointmentProviderId: string | null;
  referrer: string | null;
  inviteToken: string | null;
  providerUrl: string | null;

  bookingBothSelectedProviderId: string | null;
  bookingBothMatchingPaymentType: MatchingProvidersType;
  bookingBothAppointment: PublicAppointment | null;
  bookingBothInsuranceId: string | null;

  matchingFlow: MatchingFlowTypeEnum;
  progressPercentage: number;
  matchingPaymentType: MatchingProvidersType;
};

export type LoadActorReturnType = {
  matchId?: string;
  isLoggedIn?: boolean;
  matchingFlow: MatchingFlowTypeEnum.Marketplace;
  stateId?: string;
  shouldShowWelcomeStep?: boolean;
} | {
  matchId: string;
  isLoggedIn?: boolean;
  matchingFlow: MatchingFlowTypeEnum.Invite;
  selectedProviderId: string;
  appointment: PublicAppointment;
  inviteToken: string;
  stateId?: string;
} |  {
  matchId?: string;
  isLoggedIn?: boolean;
  matchingFlow: MatchingFlowTypeEnum.Direct;
  selectedProviderId: string;
  providerUrl: string;
  appointment: PublicAppointment;
  referrer: string;
  stateId?: string;
};

export type WhereNextData = {
  stateId: string;
};

export type InsuranceNextData = {
  insuranceId: string;
};

export type WhoNextData = {
  who: BookingForEnum;
  modalityId: string;
};

export type AgeNextData = {
  age: { id: string; ageGroup: AgeGroupEnum };
};

export type ReasonNextData = {
  specialtyIds: string[];
};

export type ProviderFiltersNextData = {
  providerFilters: ProviderFilterTypeEnum[];
};

export type ProviderIdentityFilterNextData = {
  genderIds: string[];
  faithIds: string[];
  ethnicityIds: string[];
};

export type ProviderExperienceFilterNextData = {
  communityIds: string[];
  faithIds: string[];
  ethnicityIds: string[];
};

export type LevelOfCareNextData = {
  /** If null, user is unsure about level of care */
  careType: CareType | null;
};

export type AssessmentQuestionNextData = {
  score: number;
};

export type AssessmentYesNoQuestionNextData = {
  answer: boolean | null;
};

export type AssessmentResultNextData = {
  careType: CareType;
};

export type CreateAccountNextData = {
  matchId?: string;
  isLoggedIn?: boolean;
  userId?: string;
};

export type MatchesSelectProviderData = {
  providerId: string;
  matchingPaymentType: MatchingProvidersType;
};

export type MatchesUpdateFiltersData = {
  insuranceId: string;
  specialtyIds: string[];
  identityFilters: {
    genderIds: string[];
    faithIds: string[];
    ethnicityIds: string[];
  };
  experienceFilters: {
    communityIds: string[];
    faithIds: string[];
    ethnicityIds: string[];
  };
  availabilityTimeOfDay: AvailabilityTimeOfDayInterval[];
  availabilityDayOfWeek: AvailabilityDayOfWeekInterval[];
};

export type MatchScheduleSessionData = {
  appointment: PublicAppointment;
};

export type InsuranceInfoNextData = {
  insuranceId: string;
};

export type ConfirmBookingNextData = {
  intakeFormSubmissionId: string;
};

export type OnboardingMachineEvent =
    | { type: 'Start' }
    | { type: 'Welcome.Next' }
    | { type: 'Where.Next'; data: WhereNextData }
    | { type: 'Where.SwitchToMarketplace'; data: WhereNextData }
    | { type: 'Where.NotAvailable'; data: WhereNextData }
    | { type: 'Insurance.Next'; data: InsuranceNextData }
    | { type: 'Who.Next'; data: WhoNextData }
    | { type: 'Age.Next'; data: AgeNextData }
    | { type: 'Reason.Next'; data: ReasonNextData }
    | { type: 'ProviderFilters.Next'; data: ProviderFiltersNextData }
    | { type: 'ProviderIdentityFilter.Next'; data: ProviderIdentityFilterNextData }
    | { type: 'ProviderExperienceFilter.Next'; data: ProviderExperienceFilterNextData }
    | { type: 'LevelOfCare.Next'; data: LevelOfCareNextData }
    | { type: 'AssessmentEntry.Next' }
    | { type: 'AssessmentQuestion.Next'; data: AssessmentQuestionNextData }
    | { type: 'AssessmentYesNoQuestion.Next'; data: AssessmentYesNoQuestionNextData }
    | { type: 'AssessmentResult.Next'; data: AssessmentResultNextData }
    | { type: 'AssessmentResult.ChooseAnother' }
    | { type: 'CreateAccount.Next'; data: CreateAccountNextData }
    | { type: 'Matches.SelectProvider'; data: MatchesSelectProviderData }
    | { type: 'Matches.UpdateFilters'; data: MatchesUpdateFiltersData }
    | { type: 'Match.ScheduleSession'; data: MatchScheduleSessionData }
    | { type: 'AboutYou.Next' }
    | { type: 'InsuranceInfo.Next'; data: InsuranceInfoNextData }
    | { type: 'PaymentInfo.Next' }
    | { type: 'ConfirmBooking.Next'; data: ConfirmBookingNextData }
    | { type: 'ConfirmBooking.BookingConflict' }
    | { type: 'PsychInterstitial.Next' }
    | { type: 'BookingSuccess.Next' }
    | { type: 'Back' };
